import { BrowserMultiFormatReader } from '@zxing/browser';
import { useEffect, useRef, useState } from 'react';
import LayoutWithList from '../LayoutModal/WithList';
import * as S from './style';
// icons
import { CameraRotate, Lightning, LightningSlash } from '@phosphor-icons/react';

interface IProps {
    isOpenModalCamera: boolean;
    setModalClose: () => void;
    onScanResult: (result: string | null) => void;
}

// Interface para estender MediaTrackCapabilities com torch
interface ExtendedMediaTrackCapabilities extends MediaTrackCapabilities {
    torch?: boolean;
}

export default function Camera({
    isOpenModalCamera,
    setModalClose,
    onScanResult
}: IProps) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const codeReader = useRef<BrowserMultiFormatReader>(new BrowserMultiFormatReader());
    const streamRef = useRef<MediaStream | null>(null);
    const controlsRef = useRef<any>(null);

    // Estados para controle de câmera
    const [hasCamera, setHasCamera] = useState<boolean>(false);
    const [hasFrontCamera, setHasFrontCamera] = useState<boolean>(false);
    const [hasFlash, setHasFlash] = useState<boolean>(false);
    const [isFlashOn, setIsFlashOn] = useState<boolean>(false);
    const [isFrontCamera, setIsFrontCamera] = useState<boolean>(false);
    const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
    const [scanning, setScanning] = useState<boolean>(false);

    // Função para enumerar dispositivos de câmera disponíveis
    const getAvailableDevices = async () => {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter(device => device.kind === 'videoinput');
            setDevices(videoDevices);
            setHasCamera(videoDevices.length > 0);
            setHasFrontCamera(videoDevices.length > 1);
            return videoDevices;
        } catch (err) {
            console.error("Erro ao listar dispositivos de câmera:", err);
            return [];
        }
    };

    // Limpar recursos e parar a câmera
    const cleanupResources = () => {
        // Parar a decodificação se controlsRef estiver disponível
        if (controlsRef.current) {
            try {
                controlsRef.current.stop();
                controlsRef.current = null;
            } catch (err) {
                console.error('Erro ao parar decodificação:', err);
            }
        }

        // Parar stream da câmera
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }

        // Limpar vídeo
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }

        setScanning(false);
    };

    // Função para iniciar a câmera com as configurações desejadas
    const startCamera = async (useFront: boolean = false) => {
        // Limpar recursos atuais
        cleanupResources();

        if (!videoRef.current) return;

        try {
            // Configurações da câmera
            const constraints = {
                video: {
                    facingMode: useFront ? "user" : "environment",
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                }
            };

            // Solicitar acesso à câmera
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            streamRef.current = stream;
            videoRef.current.srcObject = stream;

            // Verificar suporte a flash
            const track = stream.getVideoTracks()[0];
            if (track.getCapabilities) {
                // Cast para o tipo estendido
                const capabilities = track.getCapabilities() as ExtendedMediaTrackCapabilities;
                setHasFlash(capabilities.torch === true);
            } else {
                setHasFlash(false);
            }

            setIsFrontCamera(useFront);

            // Iniciar leitura de código quando o vídeo estiver pronto
            videoRef.current.onloadedmetadata = () => {
                startScanning();
            };
        } catch (err) {
            console.error('Erro ao iniciar câmera:', err);
            alert('Erro ao acessar a câmera: ' + (err as Error).message);
        }
    };

    // Iniciar leitura de códigos
    const startScanning = async () => {
        if (!videoRef.current || !streamRef.current) return;

        setScanning(true);

        try {
            // Usar o método correto com callback
            const controls = await codeReader.current.decodeFromVideoElement(
                videoRef.current,
                (result: any, error) => {
                    if (result) {
                        // Se encontrou um código
                        onScanResult(result.getText());
                        // Parar de escanear após encontrar um resultado
                        cleanupResources();
                    }
                    // Ignoramos erros de não encontrar códigos, que são normais durante o escaneamento
                }
            );

            // Armazenar os controles para parar o escaneamento posteriormente
            controlsRef.current = controls;
        } catch (err) {
            console.error('Erro ao configurar scanner:', err);
            setScanning(false);
        }
    };

    // Alternar entre câmera frontal e traseira
    const toggleCamera = () => {
        startCamera(!isFrontCamera);
    };

    // Controlar o flash da câmera
    const toggleFlash = () => {
        if (!hasFlash || !streamRef.current) return;

        const track = streamRef.current.getVideoTracks()[0];
        const newFlashState = !isFlashOn;

        try {
            if (track.applyConstraints) {
                // Use o método de constraints da forma que a API espera
                track.applyConstraints({
                    advanced: [{ torch: newFlashState } as any]
                })
                    .then(() => {
                        setIsFlashOn(newFlashState);
                    })
                    .catch(err => {
                        console.error("Erro ao controlar flash:", err);
                    });
            }
        } catch (err) {
            console.error("Erro ao controlar flash:", err);
        }
    };

    // Efeito para inicializar a câmera quando o modal abrir
    useEffect(() => {
        if (isOpenModalCamera) {
            const initialize = async () => {
                const devices = await getAvailableDevices();
                if (devices.length > 0) {
                    await startCamera(false); // Iniciar com câmera traseira por padrão
                }
            };

            initialize();
        }

        // Cleanup para parar a câmera quando o modal fechar
        return () => {
            cleanupResources();
        };
    }, [isOpenModalCamera]);

    const onClose = () => {
        setModalClose();
    };

    if (!isOpenModalCamera) {
        return null;
    }

    return (
        <LayoutWithList
            title='Pesquisar por código'
            subTitle='Aponte a câmera para o QR Code ou código de barras.'
            setModalClose={onClose}
        >
            <S.Content>
                {/* Área do vídeo com guias de escaneamento */}
                <div className="relative w-full">
                    <video
                        ref={videoRef}
                        style={{
                            width: '100%',
                            height: 'auto',
                            transform: isFrontCamera ? 'scaleX(-1)' : 'none'
                        }}
                        autoPlay
                        playsInline
                    />

                    {/* Guia visual para escaneamento */}
                    <div className="absolute inset-0 pointer-events-none">
                        <div style={{
                            border: '2px solid #22c55e',
                            width: '80%',
                            height: '66%',
                            margin: 'auto',
                            marginTop: '2rem',
                            borderRadius: '0.375rem',
                            opacity: 0.7
                        }}></div>
                    </div>
                </div>

                {/* Barra de controle */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '1rem',
                    backgroundColor: '#f3f4f6',
                    gap: '1rem'
                }}>
                    {hasFrontCamera && (
                        <button
                            type='button'
                            onClick={toggleCamera}
                            style={{
                                padding: '0.75rem',
                                borderRadius: '9999px',
                                backgroundColor: '#e5e7eb',
                                cursor: 'pointer'
                            }}
                        >
                            <CameraRotate size={24} color="#333" />
                        </button>
                    )}

                    {hasFlash && (
                        <button
                            type='button'
                            onClick={toggleFlash}
                            style={{
                                padding: '0.75rem',
                                borderRadius: '9999px',
                                backgroundColor: '#e5e7eb',
                                cursor: 'pointer'
                            }}
                        >
                            {isFlashOn ?
                                <Lightning size={24} color="#333" /> :
                                <LightningSlash size={24} color="#333" />
                            }
                        </button>
                    )}
                </div>

                {/* Indicador de status */}
                {scanning && (
                    <div style={{
                        textAlign: 'center',
                        padding: '0.5rem',
                        backgroundColor: '#dbeafe',
                        color: '#1e40af'
                    }}>
                        Escaneando...
                    </div>
                )}
            </S.Content>
        </LayoutWithList>
    );
}